import { useState, useCallback } from 'react';
import { Check, FlaskConical, Scale, Droplets } from 'lucide-react';
import { useExperiment } from '@/context';
import { AnalyticalBalance, Beaker, StirringRod } from '@/components/lab';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Weighing EDTA',
    content: 'EDTA (ethylenediaminetetraacetic acid) is a hexadentate ligand that forms very stable 1:1 complexes with most metal ions. The disodium salt (Na2EDTA) is commonly used because it is more soluble than the free acid. Accurate weighing is essential for preparing a solution of known concentration.'
  },
  2: {
    title: 'Transfer to Volumetric Flask',
    content: 'Use a volumetric flask for preparing standard solutions because it provides the most accurate volume measurement. Transfer the weighed EDTA quantitatively - rinse the weighing container to ensure complete transfer of all solid.'
  },
  3: {
    title: 'Adding DI Water and NaOH Pellet',
    content: 'Use deionized water to prevent contamination with metal ions that would consume your EDTA. Add about 400 mL of DI water and 1 NaOH pellet to aid dissolution. EDTA dissolves best in slightly alkaline conditions.'
  },
  4: {
    title: 'Dissolving EDTA',
    content: 'Na2EDTA dissolves best in slightly alkaline conditions. Swirl gently until all solid dissolves. If dissolution is slow, you can warm the solution slightly, but allow it to cool to room temperature before diluting to the mark.'
  },
  5: {
    title: 'Diluting to Volume',
    content: 'Add water carefully to bring the meniscus exactly to the 500 mL mark. The volumetric flask neck is narrow to improve precision. Read the meniscus at eye level for accuracy. The final concentration will be approximately 0.01 M.'
  },
  6: {
    title: 'Mixing the Solution',
    content: 'Invert the stoppered flask several times to ensure homogeneous mixing. This is critical because concentration gradients can exist in unmixed solutions. The EDTA solution is now ready but its exact concentration must be determined by standardization.'
  }
};

export default function Part1Preparation() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();
  const partState = state?.experimentData?.preparation || {
    edtaMass: 0,
    isDissolving: false,
    isDiluted: false,
    solutionPrepared: false,
    waterAdded: false,
    transferredToFlask: false
  };

  const [weighing, setWeighing] = useState(false);
  const [dissolving, setDissolving] = useState(false);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('preparation', { ...partState, ...updates });
  }, [partState, updatePartData]);

  const handleWeighEDTA = useCallback(() => {
    setWeighing(true);
    // Simulate weighing with random mass between 1.8-1.9g
    setTimeout(() => {
      const mass = 1.8 + Math.random() * 0.1;
      updateState({ edtaMass: parseFloat(mass.toFixed(4)) });
      setWeighing(false);
      completeStep('preparation-1');
    }, 1500);
  }, [updateState, completeStep]);

  const handleTransferToFlask = useCallback(() => {
    if (partState.edtaMass > 0) {
      updateState({ transferredToFlask: true });
      completeStep('preparation-2');
    }
  }, [partState.edtaMass, updateState, completeStep]);

  const handleAddWater = useCallback(() => {
    if (partState.transferredToFlask) {
      updateState({ waterAdded: true });
      completeStep('preparation-3');
    }
  }, [partState.transferredToFlask, updateState, completeStep]);

  const handleDissolve = useCallback(() => {
    if (partState.waterAdded) {
      setDissolving(true);
      setTimeout(() => {
        updateState({ isDissolving: true });
        setDissolving(false);
        completeStep('preparation-4');
      }, 2000);
    }
  }, [partState.waterAdded, updateState, completeStep]);

  const handleDiluteToMark = useCallback(() => {
    if (partState.isDissolving) {
      updateState({ isDiluted: true });
      completeStep('preparation-5');
    }
  }, [partState.isDissolving, updateState, completeStep]);

  const handleMixSolution = useCallback(() => {
    if (partState.isDiluted) {
      updateState({ solutionPrepared: true });
      completeStep('preparation-6');
    }
  }, [partState.isDiluted, updateState, completeStep]);

  const getFlaskFillLevel = () => {
    if (partState.isDiluted) return 95;
    if (partState.waterAdded) return 60;
    if (partState.transferredToFlask) return 5;
    return 0;
  };

  const getFlaskColor = () => {
    if (partState.solutionPrepared) return '#B8E6F2';
    if (partState.isDissolving) return '#C8EEF8';
    return '#7DD3FC';
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part I: EDTA Preparation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Weigh EDTA', hint: 'Weigh approximately 1.8-1.9 g Na2EDTA' },
            { id: 2, title: 'Transfer to Flask', hint: 'Transfer EDTA to 500 mL volumetric flask' },
            { id: 3, title: 'Add Water', hint: 'Add approximately 400 mL DI water + 1 NaOH pellet' },
            { id: 4, title: 'Dissolve EDTA', hint: 'Swirl until completely dissolved' },
            { id: 5, title: 'Dilute to Mark', hint: 'Add water to the 500 mL mark' },
            { id: 6, title: 'Mix Thoroughly', hint: 'Invert flask several times to homogenize' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`preparation-${step.id}`) ? 'completed' : ''} ${
                state?.currentStep === step.id ? 'active' : ''
              }`}
            >
              <div className={`step-number ${isStepCompleted(`preparation-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`preparation-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className="step-title">{step.title}</span>
                  {stepInfoContent[step.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                {state?.currentStep === step.id && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="EDTA Preparation"
        completedCount={state?.completedSteps.filter(s => s.startsWith('preparation-')).length || 0}
        totalSteps={6}
        equipment={['Analytical balance', 'Volumetric flask (500 mL)', 'Stirring rod']}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="preparation-workspace">
          <div className="equipment-row">
            {/* Analytical Balance */}
            <div className="equipment-station">
              <AnalyticalBalance
                reading={partState.edtaMass}
                unit="g"
                stable={!weighing}
                onClick={!isStepCompleted('preparation-1') ? handleWeighEDTA : undefined}
              />
              <span className="equipment-label">Analytical Balance</span>
              {partState.edtaMass > 0 && (
                <div className="reading-display">
                  Mass: {partState.edtaMass.toFixed(4)} g
                </div>
              )}
            </div>

            {/* Volumetric Flask (represented as Beaker for now) */}
            <div className="equipment-station">
              <Beaker
                size={500}
                fillLevel={getFlaskFillLevel()}
                fillColor={getFlaskColor()}
                label="500 mL Flask"
                active={
                  (!isStepCompleted('preparation-2') && partState.edtaMass > 0) ||
                  (!isStepCompleted('preparation-3') && partState.transferredToFlask) ||
                  (!isStepCompleted('preparation-5') && partState.isDissolving)
                }
                onClick={() => {
                  if (!isStepCompleted('preparation-2') && partState.edtaMass > 0) {
                    handleTransferToFlask();
                  } else if (!isStepCompleted('preparation-3') && partState.transferredToFlask) {
                    handleAddWater();
                  } else if (!isStepCompleted('preparation-5') && partState.isDissolving) {
                    handleDiluteToMark();
                  }
                }}
              />
              <span className="equipment-label">Volumetric Flask</span>
            </div>

            {/* Stirring Rod */}
            <div className="equipment-station">
              <StirringRod
                active={
                  (!isStepCompleted('preparation-4') && partState.waterAdded) ||
                  (!isStepCompleted('preparation-6') && partState.isDiluted)
                }
                onClick={() => {
                  if (!isStepCompleted('preparation-4') && partState.waterAdded) {
                    handleDissolve();
                  } else if (!isStepCompleted('preparation-6') && partState.isDiluted) {
                    handleMixSolution();
                  }
                }}
              />
              <span className="equipment-label">Stirring Rod</span>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {weighing && (
              <div className="status-message weighing">
                <Scale size={20} className="spin" />
                Weighing EDTA...
              </div>
            )}
            {dissolving && (
              <div className="status-message dissolving">
                <Droplets size={20} className="pulse" />
                Dissolving EDTA in water...
              </div>
            )}
            {partState.solutionPrepared && (
              <div className="status-message success">
                <Check size={20} />
                EDTA solution prepared! Approximate concentration: 0.01 M
              </div>
            )}
          </div>
        </div>
      </LabBench>

      <style>{`
        .preparation-workspace {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
          padding: 20px;
          width: 100%;
        }

        .equipment-row {
          display: flex;
          gap: 40px;
          align-items: flex-end;
          justify-content: center;
        }

        .equipment-station {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 12px;
          color: var(--text-secondary);
          text-align: center;
        }

        .reading-display {
          background: var(--bg-elevated);
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          color: var(--secondary);
          font-family: 'Courier New', monospace;
        }

        .status-area {
          min-height: 40px;
          display: flex;
          justify-content: center;
        }

        .status-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
        }

        .status-message.weighing,
        .status-message.dissolving {
          background: rgba(255, 165, 0, 0.2);
          color: #FFA500;
        }

        .status-message.success {
          background: rgba(0, 168, 107, 0.2);
          color: var(--secondary);
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        .pulse {
          animation: pulse 1s ease-in-out infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp4"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
